import { 
    DatakeySearchFilters, 
    CreateDatakeyRequest, 
    CreateDatakeyMultiMarketplaceRequest,
    UpdateDatakeyRequest,
    DeleteDatakeyRequest,
    DatakeyPreFillSearch,
    OperationResult,
    ModalVisibilityState,
    DatakeyMetadata,
    SearchDatakeysResponse,
    CreateDatakeyMultiMarketplaceResponse,
    GetDatakeyResponse,
    CreateDatakeyResponse,
    UpdateDatakeyResponse,
    DeleteDatakeyResponse,
    ValueOption
} from "src/model/DatakeyMetadata";

// Form field value types
type CreationFormValue = string | string[] | boolean | ValueOption[];
type PreFillFieldValue = string;
type EditingModalDatakeyValue = string | boolean;

// Action Types - Search
export const UPDATE_SEARCH_FILTERS = 'UPDATE_SEARCH_FILTERS';
export const SEARCH_DATAKEYS_REQUEST = 'SEARCH_DATAKEYS_REQUEST';
export const SEARCH_DATAKEYS_SUCCESS = 'SEARCH_DATAKEYS_SUCCESS';
export const SEARCH_DATAKEYS_ERROR = 'SEARCH_DATAKEYS_ERROR';
export const LOAD_MORE_RESULTS_REQUEST = 'LOAD_MORE_RESULTS_REQUEST';
export const LOAD_MORE_RESULTS_SUCCESS = 'LOAD_MORE_RESULTS_SUCCESS';
export const LOAD_MORE_RESULTS_ERROR = 'LOAD_MORE_RESULTS_ERROR';

// Action Types - Create Datakey
export const CREATE_DATAKEY_REQUEST = 'CREATE_DATAKEY_REQUEST';
export const CREATE_DATAKEY_SUCCESS = 'CREATE_DATAKEY_SUCCESS';
export const CREATE_DATAKEY_ERROR = 'CREATE_DATAKEY_ERROR';

// Action Types - Update Datakey
export const UPDATE_DATAKEY_REQUEST = 'UPDATE_DATAKEY_REQUEST';
export const UPDATE_DATAKEY_SUCCESS = 'UPDATE_DATAKEY_SUCCESS';
export const UPDATE_DATAKEY_ERROR = 'UPDATE_DATAKEY_ERROR';

// Action Types - Delete Datakey
export const DELETE_DATAKEY_REQUEST = 'DELETE_DATAKEY_REQUEST';
export const DELETE_DATAKEY_SUCCESS = 'DELETE_DATAKEY_SUCCESS';
export const DELETE_DATAKEY_ERROR = 'DELETE_DATAKEY_ERROR';

// Action Types - Create Multi-Marketplace (special case)
export const CREATE_DATAKEY_MULTI_REQUEST = 'CREATE_DATAKEY_MULTI_REQUEST';
export const CREATE_DATAKEY_MULTI_SUCCESS = 'CREATE_DATAKEY_MULTI_SUCCESS';
export const CREATE_DATAKEY_MULTI_ERROR = 'CREATE_DATAKEY_MULTI_ERROR';

// Action Types - Creation Form
export const UPDATE_CREATION_FORM = 'UPDATE_CREATION_FORM';
export const RESET_CREATION_FORM = 'RESET_CREATION_FORM';
export const SET_CREATE_MODE = 'SET_CREATE_MODE';

// Action Types - Pre-fill Search
export const UPDATE_PREFILL_SEARCH = 'UPDATE_PREFILL_SEARCH';
export const PREFILL_FROM_EXISTING_REQUEST = 'PREFILL_FROM_EXISTING_REQUEST';
export const PREFILL_FROM_EXISTING_SUCCESS = 'PREFILL_FROM_EXISTING_SUCCESS';
export const PREFILL_FROM_EXISTING_ERROR = 'PREFILL_FROM_EXISTING_ERROR';
export const TOGGLE_PREFILL_SECTION = 'TOGGLE_PREFILL_SECTION';

// Action Types - Operation Results
export const SET_OPERATION_RESULT = 'SET_OPERATION_RESULT';
export const CLEAR_OPERATION_RESULT = 'CLEAR_OPERATION_RESULT';

// Action Types - Modal Visibility
export const SHOW_MODAL = 'SHOW_MODAL';
export const HIDE_MODAL = 'HIDE_MODAL';
export const HIDE_ALL_MODALS = 'HIDE_ALL_MODALS';

// Action Types - Modal Data
export const SET_MODAL_DATAKEY = 'SET_MODAL_DATAKEY';
export const SET_EDITING_MODAL_DATAKEY = 'SET_EDITING_MODAL_DATAKEY';
export const CLEAR_MODAL_DATA = 'CLEAR_MODAL_DATA';
export const UPDATE_EDITING_MODAL_DATAKEY = 'UPDATE_EDITING_MODAL_DATAKEY';

// Action Interfaces (following established codebase patterns)
export interface UpdateSearchFiltersAction {
    readonly type: typeof UPDATE_SEARCH_FILTERS;
    readonly payload: DatakeySearchFilters;
}

export interface SearchDatakeysRequestAction {
    readonly type: typeof SEARCH_DATAKEYS_REQUEST;
    readonly payload: DatakeySearchFilters;
}

export interface SearchDatakeysSuccessAction {
    readonly type: typeof SEARCH_DATAKEYS_SUCCESS;
    readonly payload: SearchDatakeysResponse;
}

export interface SearchDatakeysErrorAction {
    readonly type: typeof SEARCH_DATAKEYS_ERROR;
    readonly payload: string;
}

export interface LoadMoreResultsRequestAction {
    readonly type: typeof LOAD_MORE_RESULTS_REQUEST;
    readonly payload: DatakeySearchFilters & { nextToken: string };
}

export interface LoadMoreResultsSuccessAction {
    readonly type: typeof LOAD_MORE_RESULTS_SUCCESS;
    readonly payload: SearchDatakeysResponse;
}

export interface LoadMoreResultsErrorAction {
    readonly type: typeof LOAD_MORE_RESULTS_ERROR;
    readonly payload: string;
}

export interface CreateDatakeyRequestAction {
    readonly type: typeof CREATE_DATAKEY_REQUEST;
    readonly payload: CreateDatakeyRequest;
}

export interface CreateDatakeySuccessAction {
    readonly type: typeof CREATE_DATAKEY_SUCCESS;
    readonly payload: CreateDatakeyResponse;
}

export interface CreateDatakeyErrorAction {
    readonly type: typeof CREATE_DATAKEY_ERROR;
    readonly payload: string;
}

export interface UpdateDatakeyRequestAction {
    readonly type: typeof UPDATE_DATAKEY_REQUEST;
    readonly payload: UpdateDatakeyRequest;
}

export interface UpdateDatakeySuccessAction {
    readonly type: typeof UPDATE_DATAKEY_SUCCESS;
    readonly payload: UpdateDatakeyResponse;
}

export interface UpdateDatakeyErrorAction {
    readonly type: typeof UPDATE_DATAKEY_ERROR;
    readonly payload: string;
}

export interface DeleteDatakeyRequestAction {
    readonly type: typeof DELETE_DATAKEY_REQUEST;
    readonly payload: DeleteDatakeyRequest;
}

export interface DeleteDatakeySuccessAction {
    readonly type: typeof DELETE_DATAKEY_SUCCESS;
    readonly payload: DeleteDatakeyResponse;
}

export interface DeleteDatakeyErrorAction {
    readonly type: typeof DELETE_DATAKEY_ERROR;
    readonly payload: string;
}

export interface CreateDatakeyMultiRequestAction {
    readonly type: typeof CREATE_DATAKEY_MULTI_REQUEST;
    readonly payload: CreateDatakeyMultiMarketplaceRequest;
}

export interface CreateDatakeyMultiSuccessAction {
    readonly type: typeof CREATE_DATAKEY_MULTI_SUCCESS;
    readonly payload: CreateDatakeyMultiMarketplaceResponse;
}

export interface CreateDatakeyMultiErrorAction {
    readonly type: typeof CREATE_DATAKEY_MULTI_ERROR;
    readonly payload: string;
}

export interface UpdateCreationFormAction {
    readonly type: typeof UPDATE_CREATION_FORM;
    readonly payload: { field: string; value: CreationFormValue };
}

export interface ResetCreationFormAction {
    readonly type: typeof RESET_CREATION_FORM;
}

export interface SetCreateModeAction {
    readonly type: typeof SET_CREATE_MODE;
    readonly payload: 'single' | 'multi';
}

export interface UpdatePreFillSearchAction {
    readonly type: typeof UPDATE_PREFILL_SEARCH;
    readonly payload: { field: string; value: PreFillFieldValue };
}

export interface PreFillFromExistingRequestAction {
    readonly type: typeof PREFILL_FROM_EXISTING_REQUEST;
    readonly payload: DatakeyPreFillSearch;
}

export interface PreFillFromExistingSuccessAction {
    readonly type: typeof PREFILL_FROM_EXISTING_SUCCESS;
    readonly payload: GetDatakeyResponse;
}

export interface PreFillFromExistingErrorAction {
    readonly type: typeof PREFILL_FROM_EXISTING_ERROR;
    readonly payload: string;
}

export interface TogglePreFillSectionAction {
    readonly type: typeof TOGGLE_PREFILL_SECTION;
}

export interface SetOperationResultAction {
    readonly type: typeof SET_OPERATION_RESULT;
    readonly payload: OperationResult;
}

export interface ClearOperationResultAction {
    readonly type: typeof CLEAR_OPERATION_RESULT;
}

export interface ShowModalAction {
    readonly type: typeof SHOW_MODAL;
    readonly payload: keyof ModalVisibilityState;
}

export interface HideModalAction {
    readonly type: typeof HIDE_MODAL;
    readonly payload: keyof ModalVisibilityState;
}

export interface HideAllModalsAction {
    readonly type: typeof HIDE_ALL_MODALS;
}

export interface SetModalDatakeyAction {
    readonly type: typeof SET_MODAL_DATAKEY;
    readonly payload: DatakeyMetadata | null;
}

export interface SetEditingModalDatakeyAction {
    readonly type: typeof SET_EDITING_MODAL_DATAKEY;
    readonly payload: DatakeyMetadata | null;
}

export interface ClearModalDataAction {
    readonly type: typeof CLEAR_MODAL_DATA;
}

export interface UpdateEditingModalDatakeyAction {
    readonly type: typeof UPDATE_EDITING_MODAL_DATAKEY;
    readonly payload: { field: string; value: EditingModalDatakeyValue };
}

// Union type for all DatakeyMetadata actions
export type DatakeyMetadataAction = 
    | UpdateSearchFiltersAction
    | SearchDatakeysRequestAction
    | SearchDatakeysSuccessAction
    | SearchDatakeysErrorAction
    | LoadMoreResultsRequestAction
    | LoadMoreResultsSuccessAction
    | LoadMoreResultsErrorAction
    | CreateDatakeyRequestAction
    | CreateDatakeySuccessAction
    | CreateDatakeyErrorAction
    | UpdateDatakeyRequestAction
    | UpdateDatakeySuccessAction
    | UpdateDatakeyErrorAction
    | DeleteDatakeyRequestAction
    | DeleteDatakeySuccessAction
    | DeleteDatakeyErrorAction
    | CreateDatakeyMultiRequestAction
    | CreateDatakeyMultiSuccessAction
    | CreateDatakeyMultiErrorAction
    | UpdateCreationFormAction
    | ResetCreationFormAction
    | SetCreateModeAction
    | UpdatePreFillSearchAction
    | PreFillFromExistingRequestAction
    | PreFillFromExistingSuccessAction
    | PreFillFromExistingErrorAction
    | TogglePreFillSectionAction
    | SetOperationResultAction
    | ClearOperationResultAction
    | ShowModalAction
    | HideModalAction
    | HideAllModalsAction
    | SetModalDatakeyAction
    | SetEditingModalDatakeyAction
    | ClearModalDataAction
    | UpdateEditingModalDatakeyAction;


// Action Creators - Search
export const updateSearchFilters = (filters: DatakeySearchFilters): UpdateSearchFiltersAction => ({
    type: UPDATE_SEARCH_FILTERS,
    payload: filters
});

export const searchDatakeysRequest = (filters: DatakeySearchFilters): SearchDatakeysRequestAction => ({
    type: SEARCH_DATAKEYS_REQUEST,
    payload: filters
});

export const searchDatakeysSuccess = (response: SearchDatakeysResponse): SearchDatakeysSuccessAction => ({
    type: SEARCH_DATAKEYS_SUCCESS,
    payload: response
});

export const searchDatakeysError = (error: string): SearchDatakeysErrorAction => ({
    type: SEARCH_DATAKEYS_ERROR,
    payload: error
});

export const loadMoreResultsRequest = (filters: DatakeySearchFilters, nextToken: string): LoadMoreResultsRequestAction => ({
    type: LOAD_MORE_RESULTS_REQUEST,
    payload: { ...filters, nextToken }
});

export const loadMoreResultsSuccess = (response: SearchDatakeysResponse): LoadMoreResultsSuccessAction => ({
    type: LOAD_MORE_RESULTS_SUCCESS,
    payload: response
});

export const loadMoreResultsError = (error: string): LoadMoreResultsErrorAction => ({
    type: LOAD_MORE_RESULTS_ERROR,
    payload: error
});

// Action Creators - Create Datakey
export const createDatakeyRequest = (request: CreateDatakeyRequest): CreateDatakeyRequestAction => ({
    type: CREATE_DATAKEY_REQUEST,
    payload: request
});

export const createDatakeySuccess = (response: CreateDatakeyResponse): CreateDatakeySuccessAction => ({
    type: CREATE_DATAKEY_SUCCESS,
    payload: response
});

export const createDatakeyError = (error: string): CreateDatakeyErrorAction => ({
    type: CREATE_DATAKEY_ERROR,
    payload: error
});

// Action Creators - Update Datakey
export const updateDatakeyRequest = (request: UpdateDatakeyRequest): UpdateDatakeyRequestAction => ({
    type: UPDATE_DATAKEY_REQUEST,
    payload: request
});

export const updateDatakeySuccess = (response: UpdateDatakeyResponse): UpdateDatakeySuccessAction => ({
    type: UPDATE_DATAKEY_SUCCESS,
    payload: response
});

export const updateDatakeyError = (error: string): UpdateDatakeyErrorAction => ({
    type: UPDATE_DATAKEY_ERROR,
    payload: error
});

// Action Creators - Delete Datakey
export const deleteDatakeyRequest = (request: DeleteDatakeyRequest): DeleteDatakeyRequestAction => ({
    type: DELETE_DATAKEY_REQUEST,
    payload: request
});

export const deleteDatakeySuccess = (response: DeleteDatakeyResponse): DeleteDatakeySuccessAction => ({
    type: DELETE_DATAKEY_SUCCESS,
    payload: response
});

export const deleteDatakeyError = (error: string): DeleteDatakeyErrorAction => ({
    type: DELETE_DATAKEY_ERROR,
    payload: error
});

// Action Creators - Create Multi-Marketplace (special case)
export const createDatakeyMultiRequest = (request: CreateDatakeyMultiMarketplaceRequest): CreateDatakeyMultiRequestAction => ({
    type: CREATE_DATAKEY_MULTI_REQUEST,
    payload: request
});

export const createDatakeyMultiSuccess = (response: CreateDatakeyMultiMarketplaceResponse): CreateDatakeyMultiSuccessAction => ({
    type: CREATE_DATAKEY_MULTI_SUCCESS,
    payload: response
});

export const createDatakeyMultiError = (error: string): CreateDatakeyMultiErrorAction => ({
    type: CREATE_DATAKEY_MULTI_ERROR,
    payload: error
});

// Action Creators - Creation Form
export const updateCreationForm = (field: string, value: CreationFormValue): UpdateCreationFormAction => ({
    type: UPDATE_CREATION_FORM,
    payload: { field, value }
});

export const resetCreationForm = (): ResetCreationFormAction => ({
    type: RESET_CREATION_FORM
});

export const setCreateMode = (mode: 'single' | 'multi'): SetCreateModeAction => ({
    type: SET_CREATE_MODE,
    payload: mode
});

// Action Creators - Pre-fill Search
export const updatePreFillSearch = (field: string, value: PreFillFieldValue): UpdatePreFillSearchAction => ({
    type: UPDATE_PREFILL_SEARCH,
    payload: { field, value }
});

export const preFillFromExistingRequest = (search: DatakeyPreFillSearch): PreFillFromExistingRequestAction => ({
    type: PREFILL_FROM_EXISTING_REQUEST,
    payload: search
});

export const preFillFromExistingSuccess = (datakey: GetDatakeyResponse): PreFillFromExistingSuccessAction => ({
    type: PREFILL_FROM_EXISTING_SUCCESS,
    payload: datakey
});

export const preFillFromExistingError = (error: string): PreFillFromExistingErrorAction => ({
    type: PREFILL_FROM_EXISTING_ERROR,
    payload: error
});

export const togglePreFillSection = (): TogglePreFillSectionAction => ({
    type: TOGGLE_PREFILL_SECTION
});

// Action Creators - Operation Results
export const setOperationResult = (result: OperationResult): SetOperationResultAction => ({
    type: SET_OPERATION_RESULT,
    payload: result
});

export const clearOperationResult = (): ClearOperationResultAction => ({
    type: CLEAR_OPERATION_RESULT
});

// Action Creators - Modal Visibility
export const showModal = (modalName: keyof ModalVisibilityState): ShowModalAction => ({
    type: SHOW_MODAL,
    payload: modalName
});

export const hideModal = (modalName: keyof ModalVisibilityState): HideModalAction => ({
    type: HIDE_MODAL,
    payload: modalName
});

export const hideAllModals = (): HideAllModalsAction => ({
    type: HIDE_ALL_MODALS
});

// Action Creators - Modal Data
export const setModalDatakey = (datakey: DatakeyMetadata | null): SetModalDatakeyAction => ({
    type: SET_MODAL_DATAKEY,
    payload: datakey
});

export const setEditingModalDatakey = (datakey: DatakeyMetadata | null): SetEditingModalDatakeyAction => ({
    type: SET_EDITING_MODAL_DATAKEY,
    payload: datakey
});

export const clearModalData = (): ClearModalDataAction => ({
    type: CLEAR_MODAL_DATA
});

export const updateEditingModalDatakey = (field: string, value: EditingModalDatakeyValue): UpdateEditingModalDatakeyAction => ({
    type: UPDATE_EDITING_MODAL_DATAKEY,
    payload: { field, value }
});
